import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth, useTheme } from '../../../context';
import { DARK_MODE_ENABLED } from '../../../config/features';
import './Sidebar.css';
import logo from '../../../styles/logo-sidebar.webp';

const COMMUNITY_URL = 'https://awholefamilymatter.com/';

// Sidebar structure (App Improvements, Oct 2026). A group without a label is
// set off by spacing only.
const NAV_GROUPS = [
  {
    key: 'work',
    label: 'Collective Work App',
    items: [
      { to: '/dashboard', label: 'Dashboard', icon: 'dashboard' },
      { to: '/feed', label: 'Work Stream', icon: 'explore' },
      { to: '/teams', label: 'Care Teams', icon: 'group', tour: 'nav-teams' },
    ],
  },
  {
    key: 'pillars',
    items: [
      { to: '/progress', label: 'Care Team Planning', icon: 'analytics', tour: 'nav-planning' },
      { to: '/funding', label: 'Care Fund Pooling', icon: 'savings' },
      { to: '/workbook', label: 'Advocacy Learning', icon: 'menu_book' },
      { to: '/sponsor-a-care-team', label: 'Sponsor A Care Team', icon: 'volunteer_activism' },
      { to: COMMUNITY_URL, label: 'Community Action Center', icon: 'forum', external: true },
    ],
  },
  {
    key: 'account',
    label: 'Personal Account',
    items: [
      { to: '/profile', label: 'My Profile', icon: 'person' },
      { to: '/my-recordings', label: 'My Recordings', icon: 'videocam' },
      { to: '/account-settings', label: 'My Account Settings', icon: 'settings' },
    ],
  },
  {
    key: 'support',
    items: [
      { to: '/help', label: 'Help & Support', icon: 'help_outline', tour: 'nav-help' },
      { to: '/legal', label: 'Privacy & Legal', icon: 'shield' },
    ],
  },
];

const Sidebar = ({ isOpen, onClose, isMobileOrFocus }) => {
  const { user, logout, isLoggingOut } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const [isCollapsed, setIsCollapsed] = useState(() => {
    return localStorage.getItem('awfm-sidebar-collapsed') === 'true';
  });

  const toggleCollapsed = () => {
    setIsCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('awfm-sidebar-collapsed', String(next));
      return next;
    });
  };

  const handleLogout = async () => {
    await logout();
    if (onClose) onClose();
    navigate('/login');
  };

  const handleSignIn = () => {
    if (onClose) onClose();
    navigate('/login');
  };

  const handleLinkClick = () => {
    if (onClose) onClose();
  };

  const isActive = (path) => {
    if (path === '/dashboard' || path === '/') {
      return location.pathname === '/' || location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(path);
  };

  // Determine sidebar class based on mode
  const sidebarClass = isMobileOrFocus
    ? `sidebar sidebar--mobile ${isOpen ? 'sidebar--open' : ''}`
    : `sidebar sidebar--desktop${isCollapsed ? ' sidebar--collapsed' : ''}`;

  return (
    <>
      {/* Backdrop - only for mobile/focus mode */}
      {isMobileOrFocus && isOpen && (
        <div
          className="sidebar__backdrop"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside className={sidebarClass}>
        {/* Header with logo */}
        <div className="sidebar__header">
          <div className="sidebar__logo-section" onClick={() => navigate('/dashboard')}>
            <img src={logo} alt="AWFM Logo" className="sidebar__logo" />
            <span className="sidebar__brand">a whole <span className="sidebar__brand-bold">family matter</span></span>
          </div>
          {isMobileOrFocus && (
            <button
              className="sidebar__close-btn"
              onClick={onClose}
              aria-label="Close sidebar"
            >
              <span className="material-icons">close</span>
            </button>
          )}
        </div>

        {/* Navigation Menu */}
        <nav className="sidebar__nav" aria-label="Main navigation">
          {NAV_GROUPS.map((group) => (
            <React.Fragment key={group.key}>
              {group.label
                ? <div className="sidebar__section-label">{group.label}</div>
                : <div className="sidebar__group-gap" aria-hidden="true" />}
              {group.items.map((item) => {
                const inner = (
                  <>
                    <span className="sidebar__nav-icon">
                      <span className="material-icons">{item.icon}</span>
                    </span>
                    <span className="sidebar__nav-label">{item.label}</span>
                    {item.external && <span className="material-icons sidebar__nav-ext" aria-hidden="true">open_in_new</span>}
                  </>
                );
                if (item.external) {
                  return (
                    <a
                      key={item.label}
                      href={item.to}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="sidebar__nav-item"
                      onClick={handleLinkClick}
                      data-tooltip={item.label}
                    >
                      {inner}
                    </a>
                  );
                }
                const active = isActive(item.to);
                return (
                  <Link
                    key={item.label}
                    to={item.to}
                    className={`sidebar__nav-item ${active ? 'sidebar__nav-item--active' : ''}`}
                    onClick={handleLinkClick}
                    aria-current={active ? 'page' : undefined}
                    data-tooltip={item.label}
                    data-tour={item.tour}
                  >
                    {inner}
                  </Link>
                );
              })}
            </React.Fragment>
          ))}

          <div className="sidebar__group-gap" aria-hidden="true" />
          {user ? (
            <button
              className="sidebar__nav-item sidebar__nav-item--danger"
              onClick={handleLogout}
              disabled={isLoggingOut}
              data-tooltip="Logout"
            >
              <span className="sidebar__nav-icon">
                <span className="material-icons">{isLoggingOut ? 'hourglass_empty' : 'logout'}</span>
              </span>
              <span className="sidebar__nav-label">{isLoggingOut ? 'Logging out...' : 'Logout'}</span>
            </button>
          ) : (
            <button
              className="sidebar__nav-item"
              onClick={handleSignIn}
              data-tooltip="Sign in"
            >
              <span className="sidebar__nav-icon">
                <span className="material-icons">login</span>
              </span>
              <span className="sidebar__nav-label">Sign in</span>
            </button>
          )}
        </nav>

        {/* Theme Toggles - compact row (hidden while dark mode is disabled) */}
        {DARK_MODE_ENABLED && (
        <div className="sidebar__theme-row">
          <button
            className={`sidebar__theme-pill ${theme === 'dark' ? 'sidebar__theme-pill--active' : ''}`}
            onClick={toggleTheme}
            aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
            title={theme === 'light' ? 'Night Mode' : 'Light Mode'}
          >
            <span className="material-icons">
              {theme === 'light' ? 'dark_mode' : 'light_mode'}
            </span>
            <span className="sidebar__theme-label">{theme === 'light' ? 'Night Mode' : 'Light Mode'}</span>
          </button>
        </div>
        )}

        {/* Collapse Toggle - desktop only */}
        {!isMobileOrFocus && (
          <button
            className="sidebar__collapse-btn"
            onClick={toggleCollapsed}
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <span className="material-icons">
              {isCollapsed ? 'chevron_right' : 'chevron_left'}
            </span>
            <span className="sidebar__collapse-label">Collapse</span>
          </button>
        )}

        {/* Footer */}
        <div className="sidebar__footer">
          <p className="sidebar__app-name">A Whole Family Matter</p>
          <p className="sidebar__version">Collective Work App v1.0</p>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
